/**
 * Type definitions for the 360 Product Viewer
 */

// ============================================
// TRANSLATABLE TEXT
// ============================================

// Desteklenen diller (sıra önemli: TR kaynak dil, EN yabancı diller için ilk yedek)
export const LANGUAGES = ['tr', 'en', 'fr', 'de'] as const;
export type Language = typeof LANGUAGES[number];

// Çeviri yoksa sırayla denenecek diller
export const LANGUAGE_FALLBACKS: Record<Language, Language[]> = {
  tr: ['tr'],
  en: ['en', 'tr'],
  fr: ['fr', 'en', 'tr'],
  de: ['de', 'en', 'tr'],
};

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

// Çevrilebilir metin tipi
export interface TranslatableText {
  tr: string;
  en?: string;  // Opsiyonel, yoksa TR kullanılır
  fr?: string;  // Opsiyonel, yoksa EN, o da yoksa TR kullanılır
  de?: string;  // Opsiyonel, yoksa EN, o da yoksa TR kullanılır
}

// Çevrilebilir metni çöz
export function resolveText(
  text: string | TranslatableText | undefined,
  lang: Language
): string {
  if (!text) return '';
  if (typeof text === 'string') return text;
  for (const fallback of LANGUAGE_FALLBACKS[lang]) {
    const value = text[fallback];
    if (value) return value;
  }
  return '';
}

// Form state'i için tüm dillerin düz string hali
export type LangValues = Record<Language, string>;

export function emptyLangValues(): LangValues {
  return { tr: '', en: '', fr: '', de: '' };
}

// Firestore'daki değeri form state'ine çevir
export function toLangValues(text: string | TranslatableText | undefined): LangValues {
  if (!text) return emptyLangValues();
  if (typeof text === 'string') return { ...emptyLangValues(), tr: text };
  return { tr: text.tr || '', en: text.en || '', fr: text.fr || '', de: text.de || '' };
}

// Form state'ini Firestore'a yazılacak değere çevir.
// Boş dilleri ve yedek dilin zaten vereceği değeri yazmaz (Firebase undefined kabul etmez);
// sadece TR kalırsa düz string döner.
export function fromLangValues(values: LangValues): string | TranslatableText {
  const result: TranslatableText = { tr: values.tr.trim() };
  for (const lang of LANGUAGES) {
    if (lang === 'tr') continue;
    const value = values[lang].trim();
    if (value && value !== resolveText(result, lang)) {
      result[lang] = value;
    }
  }
  return Object.keys(result).length === 1 ? result.tr : result;
}

// ============================================
// PRODUCT CONFIGURATION
// ============================================

// Renk Varyantı
export interface ColorVariant {
  id: string;
  name: string | TranslatableText;
  color: string;          // CSS renk kodu
  pattern?: string;       // Doku görseli (base64 veya URL) - varsa color yerine kullanılır
  specs?: VariantSpec[];  // Renge özgü özellikler (sabit specs'e eklenir)
  isDisabled?: boolean;   // Stok tükendi
  isDefault?: boolean;    // Favori (sayfa açılışında seçili)
}

// Özel Varyant (boyut, materyal, paket içeriği vb.)
export interface CustomVariant {
  id: string;
  name: string | TranslatableText;  // "160x200", "Premium Paket" vb.
  specs?: VariantSpec[];  // Dinamik özellikler
  isDisabled?: boolean;
  isDefault?: boolean;
}

// Varyant özellikleri (dinamik key-value)
export interface VariantSpec {
  label: string | TranslatableText;  // "En", "Boy", "Ağırlık" vb.
  value: string | TranslatableText;  // "150 cm", "25 kg" vb.
}

// Renk bazlı override (her renk için özel ayarlar)
export interface VariantOverride {
  count?: number;       // Fotoğraf sayısı override
  extension?: string;   // Dosya uzantısı override
  wrapAround?: boolean; // 360° döndürme override
  galleryMode?: boolean; // true = galeri modu (statik fotoğraflar), false/undefined = 360° döndürme
}

// Complete product configuration
export interface ProductConfig {
  // Basic Info
  name: string | TranslatableText;
  description?: string | TranslatableText;
  badge?: string | TranslatableText;

  // Varyantlar
  colorVariants?: ColorVariant[];
  customVariants?: CustomVariant[];

  // Özellik Tablosu
  specsTitle?: string | TranslatableText;  // Tablo başlığı
  specs?: VariantSpec[];   // Sabit özellikler (tüm varyantlar için ortak)

  // Image Settings
  images: {
    basePath: string;      // Will be combined with env base URL
    count: number;         // Number of frames per variant
    extension: string;     // "jpg", "png", "webp"
  };

  // Rotation Settings
  wrapAround?: boolean;    // true = 360° sonsuz döngü, false = sınırlı döndürme (default: true)
  galleryMode?: boolean;   // true = statik fotoğraf galerisi, false = 360° döndürme (default: false)

  // Renk bazlı override'lar (Key: "colorId")
  variantOverrides?: Record<string, VariantOverride>;

  // Features (optional)
  features?: FeatureItem[];

  // CTA Button (optional)
  cta?: CTAConfig;
}

export interface FeatureItem {
  text: string | TranslatableText;
}

// CTA Button Action Types
// Note: 'link_blank' is deprecated but kept for backward compatibility with existing data
export type CTAActionType = 'link' | 'link_blank' | 'email' | 'whatsapp';

export interface CTAConfig {
  label: string | TranslatableText;
  action: CTAActionType;
  value: string; // URL, email address, or phone number (not translatable)
  emailSubject?: string | TranslatableText; // For email action
  messageTemplate?: string | TranslatableText; // Template with variables: {urun}, {renk}, {link}
}

// ============================================
// VIEWER CONFIGURATION
// ============================================

// Kombinasyon varyantı (renk + özel varyant)
export interface VariantConfig {
  id: string;             // Kombinasyon ID: "colorId_customId"
  colorId: string;
  customId: string;
  name: string;           // Görüntü adı
  color: string;          // CSS color for UI (renk varyantından)
  imagePath: string;      // Full path: {BASE_URL}/{basePath}/{colorId}/{customId}
  frameCount: number;
  filePattern: string;    // Pattern like "frame-{n}.jpg"
  wrapAround: boolean;    // true = 360° sonsuz döngü, false = sınırlı
  galleryMode: boolean;   // true = galeri modu (statik fotoğraflar)
}

// Configuration for the entire viewer
export interface ViewerConfig {
  container: HTMLElement;
  variants: VariantConfig[];
  initialVariant?: string;  // Kombinasyon ID
  initialFrame?: number;
  autoRotate?: boolean;
  autoRotateSpeed?: number; // ms per frame
  sensitivity?: number; // Pixels per frame change
  enableTouch?: boolean;
  enableMouse?: boolean;
  preloadFrames?: number; // Number of frames to preload immediately
  // Zoom options
  maxZoom?: number; // Maximum zoom level (default: 3)
  enableZoom?: boolean; // Enable zoom functionality (default: true)
  // Rotation options
  wrapAround?: boolean; // true = 360° sonsuz döngü, false = sınırlı (default: true)
  // UI text
  dragHintText?: string; // Text shown on drag hint (default: "Döndürmek için sürükle")
  loadingText?: string; // Text shown on loading overlay (default: "Yükleniyor")
  onFrameChange?: (frame: number) => void;
  onVariantChange?: (variant: string) => void;
  onLoadProgress?: (progress: number, variant: string) => void;
  onZoomChange?: (scale: number) => void;
  onReady?: () => void;
}

// Image cache entry
export interface CachedImage {
  src: string;
  element: HTMLImageElement;
  loaded: boolean;
  error: boolean;
}

// Loading state for a variant
export interface LoadingState {
  total: number;
  loaded: number;
  failed: number;
  inProgress: boolean;
}

// Rotation state
export interface RotationState {
  currentFrame: number;
  isDragging: boolean;
  startX: number;
  lastX: number;
  velocity: number;
}

// Zoom state
export interface ZoomState {
  scale: number;
  offsetX: number;
  offsetY: number;
  isPinching: boolean;
  isPanning: boolean;
}

// Event types for internal communication
export type ViewerEventType =
  | 'frameChange'
  | 'variantChange'
  | 'loadStart'
  | 'loadProgress'
  | 'loadComplete'
  | 'loadError'
  | 'dragStart'
  | 'dragEnd'
  | 'zoomChange'
  | 'panStart'
  | 'panEnd'
  | 'ready';

export interface ViewerEvent {
  type: ViewerEventType;
  data?: unknown;
}
